// Die untere Leiste — Start plus die selbst angehefteten Module.
//
// Welche Module hier stehen, entscheidet jeder selbst: Kachel auf dem
// Startbildschirm lang drücken und hierher ziehen. Der Fahrer heftet Tour und
// Stempeluhr an, das Büro Kunden und Kasse. Wir raten nicht, was "die wichtigen"
// Module sind — die Leute wissen es besser.
//
// "Start" steht immer ganz links und lässt sich nicht lösen: Von dort führt der
// Weg zurück zum Kachelfeld. Wer sich die Leiste leerräumt, darf sich nicht
// aussperren können.
//
// Was hier hängt, steht NICHT mehr auf dem Kachelfeld — ein Modul an zwei
// Stellen gleichzeitig wäre doppelt, nicht nützlicher. Lang drücken löst es
// wieder, dann ist es oben zurück.

import { useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { usePins, useSpeicherePins } from "@/lib/pins";
import { modulNachId, MAX_PINS } from "@/lib/module-registry";
import { useUngeleseneNews } from "@/lib/news";
import { useOffeneQs } from "@/lib/qs";
import { usePinDrag, LEISTE_MARKE } from "./PinDrag";
import { cn } from "@/design-system/ui";

const DRUCKDAUER = 450;

export function BottomBar() {
  const { data: pins = [] } = usePins();
  const speichern = useSpeicherePins();
  const { pathname } = useLocation();
  const { data: ungelesen = 0 } = useUngeleseneNews();
  const { data: offeneQs = 0 } = useOffeneQs();
  const drag = usePinDrag();

  const badges: Record<string, number> = { news: ungelesen, qs: offeneQs };
  const module = pins.map(modulNachId).filter((m): m is NonNullable<typeof m> => !!m);

  /** Wird gerade eine Kachel hierher gezogen? */
  const zielt = !!drag.modulId;
  const bereit = zielt && drag.ueberLeiste;
  /** Freie Plätze — beim Ziehen sichtbar, damit man SIEHT, dass noch was passt. */
  const frei = Math.max(0, MAX_PINS - module.length);

  async function loesen(id: string) {
    await speichern.mutateAsync(pins.filter((x) => x !== id)).catch(() => {
      /* Der Hook rollt zurück; ein Toast-Layer existiert hier noch nicht. */
    });
  }

  return (
    <nav
      aria-label="Schnellzugriff"
      data-testid={LEISTE_MARKE}
      className={cn(
        "shrink-0 border-t bg-ink-900/95 backdrop-blur transition-colors duration-fast",
        "pb-[env(safe-area-inset-bottom)]",
        bereit
          ? "border-gruen-500 bg-gruen-500/10"
          : zielt
            ? "border-gruen-500/40"
            : "border-ink-700/70"
      )}
    >
      <ul className="mx-auto flex max-w-2xl items-stretch">
        <li className="flex-1">
          <Knopf to="/crm" label="Start" aktiv={pathname === "/crm"}>
            <IconStart />
          </Knopf>
        </li>

        {module.map((m) => (
          <li key={m.id} className="flex-1">
            <Knopf
              to={m.to}
              label={kurz(m.label)}
              // Nicht `pathname === m.to`: Auf /crm/kunden/<id> soll "Kunden"
              // weiterhin als aktiv erkennbar sein.
              aktiv={pathname === m.to || pathname.startsWith(m.to + "/")}
              badge={badges[m.id]}
              onLoesen={() => loesen(m.id)}
            >
              <span className="[&>svg]:h-[22px] [&>svg]:w-[22px]">{m.icon}</span>
            </Knopf>
          </li>
        ))}

        {/* Leere Plätze nur WÄHREND des Ziehens. Sonst stünden dort dauerhaft
            gestrichelte Kästen herum und die Leiste sähe unfertig aus. */}
        {zielt &&
          Array.from({ length: frei }).map((_, i) => (
            <li key={`frei-${i}`} className="flex-1">
              <div className="flex h-14 items-center justify-center">
                <div
                  className={cn(
                    "grid h-9 w-9 place-items-center rounded-lg border border-dashed transition-colors duration-fast",
                    bereit ? "border-gruen-400 text-gruen-400" : "border-ink-600 text-ink-600"
                  )}
                >
                  <span className="text-lg leading-none">+</span>
                </div>
              </div>
            </li>
          ))}
      </ul>

      {/* Beim Ziehen sagen, was passiert — und was nicht mehr passt. */}
      {zielt && (
        <p
          className={cn(
            "pb-1.5 text-center text-[11px] font-medium transition-colors duration-fast",
            bereit ? "text-gruen-400" : "text-ink-400"
          )}
        >
          {frei === 0
            ? "Leiste voll — loslassen, um zu tauschen"
            : bereit
              ? "Loslassen zum Anheften"
              : `Hierher ziehen · noch ${frei} Platz${frei === 1 ? "" : "e"}`}
        </p>
      )}
    </nav>
  );
}

function Knopf({
  to,
  label,
  aktiv,
  badge,
  onLoesen,
  children,
}: {
  to: string;
  label: string;
  aktiv: boolean;
  badge?: number;
  /** Fehlt bei "Start" — der lässt sich nicht lösen. */
  onLoesen?: () => void;
  children: React.ReactNode;
}) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const schluckeKlick = useRef(false);
  const [fragt, setFragt] = useState(false);

  const abbrechen = () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    start.current = null;
  };

  return (
    <div className="relative">
      <NavLink
        to={to}
        onClick={(e) => {
          if (schluckeKlick.current) {
            e.preventDefault();
            schluckeKlick.current = false;
          }
        }}
        onContextMenu={(e) => {
          if (!onLoesen) return;
          e.preventDefault();
          setFragt(true);
        }}
        onPointerDown={(e) => {
          if (!onLoesen) return;
          if (e.pointerType === "mouse") return; // Maus hat den Rechtsklick
          start.current = { x: e.clientX, y: e.clientY };
          timer.current = window.setTimeout(() => {
            schluckeKlick.current = true;
            setFragt(true);
            navigator.vibrate?.(12);
          }, DRUCKDAUER);
        }}
        onPointerMove={(e) => {
          if (!start.current) return;
          const d = Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y);
          if (d > 10) abbrechen();
        }}
        onPointerUp={abbrechen}
        onPointerCancel={abbrechen}
        className={cn(
          // 56 px hoch — unter 44 px wird es am Daumen zur Glückssache.
          "flex h-14 flex-col items-center justify-center gap-0.5",
          "transition-colors duration-fast",
          "select-none [-webkit-touch-callout:none]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gruen-500/60",
          aktiv ? "text-gruen-400" : "text-ink-400 hover:text-ink-200"
        )}
      >
        <span className="relative">
          {children}
          {badge != null && badge > 0 && (
            <span className="absolute -right-2 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-semibold tabular-nums text-white">
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </span>
        <span className="text-[11px] font-medium leading-none">{label}</span>
      </NavLink>

      {fragt && onLoesen && (
        <>
          {/* pointerdown, nicht click: Der synthetische Klick nach dem langen
              Druck würde sonst sofort hier landen und das Menü zuschlagen,
              bevor es jemand gesehen hat. */}
          <div
            className="fixed inset-0 z-40"
            onPointerDown={() => setFragt(false)}
            aria-hidden
          />
          <div
            role="menu"
            className={cn(
              "absolute bottom-full left-1/2 z-50 mb-2 w-max -translate-x-1/2",
              "animate-fade-in rounded-xl border border-ink-700 bg-ink-850 p-1 shadow-2xl"
            )}
          >
            <button
              role="menuitem"
              onClick={() => {
                setFragt(false);
                onLoesen();
              }}
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-ink-100 transition-colors duration-fast hover:bg-ink-800"
            >
              <IconNadelAus />
              Von der Leiste lösen
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * "Buchhaltung & Kasse" passt nicht unter ein 22-px-Symbol. Statt zu kürzen und
 * zu hoffen, wird gezielt abgekürzt — abgeschnittene Wörter mit Auslassungspunkt
 * sehen in einer Leiste immer nach Fehler aus.
 */
function kurz(label: string) {
  const KURZ: Record<string, string> = {
    "Buchhaltung & Kasse": "Kasse",
    "Produkte & Preise": "Produkte",
    Kundenstamm: "Kunden",
    Tagestouren: "Touren",
    Zeiterfassung: "Zeiten",
    Neuigkeiten: "News",
  };
  return KURZ[label] ?? label;
}

function IconNadelAus() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-ink-400"
      aria-hidden
    >
      <path d="M12 17v5" />
      <path d="M9 10.5V4h6v6.5l2.5 3.5h-11L9 10.5Z" />
      <path d="m4 4 16 16" />
    </svg>
  );
}

function IconStart() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3.5" y="3.5" width="7" height="7" rx="2" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="2" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="2" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="2" />
    </svg>
  );
}
